/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import Alert from 'bundles/authentication-common/components/Alert';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Apple from 'bundles/authentication-common/components/sso/Apple';
import Facebook from 'bundles/authentication-common/components/sso/Facebook';
import Google from 'bundles/authentication-common/components/sso/Google';
import { AUTHENTICATION_MODAL_MODES, LEARNER_HELP_CENTER_LINK } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';

import _t from 'i18n!nls/authentication';

export type Props = {
  accountType: 'apple' | 'facebook' | 'google';
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSSOError: (error: $TSFixMe) => void;
  onSSOSuccess: (response: $TSFixMe) => void;
};

type State = {
  error: string;
};

export const HANDLED_ERRORS = ['unableToGetProfile', 'unknownError'];

const Buttons = {
  apple: Apple,
  facebook: Facebook,
  google: Google,
};

const styles = {
  container: css`
    display: grid;
    grid-gap: 24px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 32px 16px;
  `,
  description: css`
    margin-bottom: 0;
    text-align: center;
  `,
  navigation: css`
    margin-bottom: 0;
    text-align: center;
  `,
  title: css`
    color: var(--cds-color-neutral-primary);
    font-family: 'Open Sans', sans-serif;
    font-size: 24px;
    line-height: 30px;
    text-align: center;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
};

function errorMessagesFactory() {
  return {
    unableToGetProfile: _t('Unable to get profile. Please try again.'),
    unknownError: (
      <FormattedMessage
        message={_t(
          'Something went wrong. Please refresh the page and try again. If the problem persists, visit the {helpCenterLink}.'
        )}
        helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
      />
    ),
  };
}

function serviceNamesFactory() {
  return {
    apple: _t('Apple'),
    facebook: _t('Facebook'),
    google: _t('Google'),
  };
}

class LinkedAccount extends React.Component<Props, State> {
  state = { error: '' };

  handleSSOError = (error: $TSFixMe) => {
    const { onSSOError } = this.props;

    const { code } = error;

    if (HANDLED_ERRORS.includes(code)) {
      this.setState({ error: code });
    } else {
      onSSOError(error);
    }
  };

  navigateToLogin = () => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  render() {
    const { accountType, onSSOSuccess } = this.props;
    const { error } = this.state;

    const errorMessages = errorMessagesFactory();
    const serviceNames = serviceNamesFactory();

    const Service = Buttons[accountType] || Google;

    return (
      <section css={styles.container}>
        <header>
          <h1 css={styles.title}>
            <FormattedMessage
              message={_t('You already have a Coursera account through {service}')}
              service={serviceNames[accountType]}
            />
          </h1>
        </header>
        {/* @ts-expect-error TS7016 TODO: error do not exist in errorMessages */}
        {error && <Alert>{errorMessages[error]}</Alert>}
        <p css={styles.description}>
          <FormattedMessage
            message={_t(
              'Sign into your Coursera account through {service} below. You can change account linking preferences in your account settings'
            )}
            service={serviceNames[accountType]}
          />
        </p>
        <Service onError={this.handleSSOError} onSuccess={onSSOSuccess} />
        <p css={styles.navigation}>
          <FormattedMessage
            message={_t('Back to {loginLink}.')}
            loginLink={
              <LinkButton onClick={this.navigateToLogin} type="button">
                {_t('Log in')}
              </LinkButton>
            }
          />
        </p>
      </section>
    );
  }
}

export default LinkedAccount;
